import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { fetchGuideReviewDisplayUrl } from "./fetchGuideReviewImage";

const REVIEW_IMAGE_HEIGHT = "200px";

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: ${REVIEW_IMAGE_HEIGHT};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid #ddd;
    background: #f5f5f5;
    overflow: hidden;
`;

const ReviewImage = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: contain;
    cursor: zoom-in;
`;

const LightboxOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    cursor: zoom-out;
`;

const EnlargedImage = styled.img`
    max-width: 95vw;
    max-height: 95vh;
    object-fit: contain;
    border-radius: 8px;
    cursor: default;
`;

const Placeholder = styled.div`
    width: 100%;
    height: ${REVIEW_IMAGE_HEIGHT};
    border-radius: 8px;
    border: 1px dashed #ccc;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 0.9rem;
    background: #fafafa;
`;

const DeleteButton = styled.button`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.35rem 0.6rem;
    cursor: pointer;
    font-size: 0.85rem;

    &:hover {
        background-color: #c82333;
    }
`;

interface GuideReviewImageProps {
    reviewId: string;
    alt?: string;
    onDelete?: (reviewId: string) => void;
}

const GuideReviewImage: React.FC<GuideReviewImageProps> = ({
    reviewId,
    alt,
    onDelete,
}) => {
    const [displayUrl, setDisplayUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isEnlarged, setIsEnlarged] = useState(false);
    const objectUrlRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const loadImage = async () => {
            setLoading(true);
            setError(false);
            setDisplayUrl(null);

            try {
                const objectUrl = await fetchGuideReviewDisplayUrl(reviewId);
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                if (objectUrlRef.current) {
                    URL.revokeObjectURL(objectUrlRef.current);
                }

                objectUrlRef.current = objectUrl;
                setDisplayUrl(objectUrl);
            } catch {
                if (!cancelled) {
                    setError(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            cancelled = true;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, [reviewId]);

    useEffect(() => {
        if (!isEnlarged) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsEnlarged(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isEnlarged]);

    if (loading) {
        return <Placeholder>Loading...</Placeholder>;
    }

    if (error || !displayUrl) {
        return <Placeholder>Image unavailable</Placeholder>;
    }

    return (
        <>
            <ImageWrapper>
                <ReviewImage
                    src={displayUrl}
                    alt={alt || "Guide review"}
                    title="Click to enlarge"
                    onClick={() => setIsEnlarged(true)}
                />
                {onDelete && (
                    <DeleteButton
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(reviewId);
                        }}
                    >
                        Delete
                    </DeleteButton>
                )}
            </ImageWrapper>

            {isEnlarged && (
                <LightboxOverlay onClick={() => setIsEnlarged(false)}>
                    <EnlargedImage
                        src={displayUrl}
                        alt={alt || "Guide review"}
                        onClick={(event) => event.stopPropagation()}
                    />
                </LightboxOverlay>
            )}
        </>
    );
};

export default GuideReviewImage;
